import { useRouter } from "next/navigation";
import { useTranslations, useLocale } from "next-intl";
import { Case } from "@/lib/api";
import { CardIcon } from "@/components/CardIcon";

const difficultyConfig: Record<string, { key: "beginner" | "intermediate" | "advanced"; bg: string; text: string; border: string }> = {
  beginner:     { key: "beginner",     bg: "bg-emerald-50",  text: "text-emerald-700", border: "border-emerald-200" },
  intermediate: { key: "intermediate", bg: "bg-amber-50",    text: "text-amber-700",   border: "border-amber-200"   },
  advanced:     { key: "advanced",     bg: "bg-rose-50",     text: "text-rose-700",    border: "border-rose-200"    },
};

export function CaseCard({ c, sessionCount, onStart, view = "card" }: { c: Case; sessionCount: number; onStart: () => void; view?: "card" | "list" }) {
  const router = useRouter();
  const t = useTranslations("dashboard.caseCard");
  const locale = useLocale();
  const diff = difficultyConfig[c.difficulty_level] ?? difficultyConfig.beginner;
  const complaint = locale === "tr" && c.presenting_complaint_tr ? c.presenting_complaint_tr : c.presenting_complaint;

  const badge = (
    <span className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${diff.bg} ${diff.text} ${diff.border} shrink-0`}>
      {t(`difficulty.${diff.key}`)}
    </span>
  );

  if (view === "list") {
    return (
      <div
        className="bg-white rounded-2xl p-4 border border-[var(--border)] flex items-center gap-4 transition-all duration-200"
        style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
        onMouseOver={e => (e.currentTarget.style.boxShadow = '0 4px 20px rgba(15,76,76,0.08), 0 1px 4px rgba(0,0,0,0.04)')}
        onMouseOut={e => (e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)')}
      >
        <CardIcon type="patient" size={40} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2.5">
            <h3 className="font-semibold text-base text-[var(--text-primary)] truncate" style={{ fontFamily: "var(--font-lora)" }}>
              {c.name}, {c.age}
            </h3>
            {badge}
          </div>
          <p className="text-sm text-[var(--text-secondary)] truncate mt-0.5">{complaint}</p>
        </div>
        <span className="hidden lg:block text-xs text-[var(--text-muted)] shrink-0 whitespace-nowrap">
          {t("sessionsCompleted", { count: sessionCount })}
        </span>
        <div className="flex gap-2 shrink-0">
          <button
            onClick={onStart}
            className="text-white text-sm font-semibold rounded-xl px-4 py-2 transition-colors whitespace-nowrap"
            style={{ background: 'var(--accent)' }}
            onMouseOver={e => (e.currentTarget.style.background = 'var(--accent-mid)')}
            onMouseOut={e => (e.currentTarget.style.background = 'var(--accent)')}
          >
            {t("startSession")}
          </button>
          <button
            onClick={() => router.push(`/sessions/${c.id}?patient=${encodeURIComponent(c.name)}`)}
            className="px-3 py-2 text-xs font-semibold rounded-xl border border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--surface-alt)] hover:text-[var(--text-primary)] transition-colors"
            title={t("viewHistory")}
          >
            {t("sessions")}
          </button>
          <button
            onClick={() => router.push(`/feedback/${c.id}?patient=${encodeURIComponent(c.name)}`)}
            className="px-3 py-2 text-xs font-semibold rounded-xl border border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--surface-alt)] hover:text-[var(--text-primary)] transition-colors"
          >
            {t("feedback")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-6 flex flex-col gap-4 border border-[var(--border)] hover:-translate-y-1 transition-all duration-200 group" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }} onMouseOver={e => (e.currentTarget.style.boxShadow = '0 4px 20px rgba(15,76,76,0.08), 0 1px 4px rgba(0,0,0,0.04)')} onMouseOut={e => (e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)')}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-4 min-w-0">
          <CardIcon type="patient" />
          <h3 className="font-semibold text-lg text-[var(--text-primary)] truncate" style={{ fontFamily: "var(--font-lora)" }}>
            {c.name}, {c.age}
          </h3>
        </div>
        {badge}
      </div>

      <p className="text-sm text-[var(--text-secondary)] leading-relaxed flex-1">{complaint}</p>

      <div className="flex items-center text-xs text-[var(--text-muted)]">
        <span>{t("sessionsCompleted", { count: sessionCount })}</span>
      </div>

      <div className="flex gap-2">
        <button
          onClick={onStart}
          className="flex-1 text-white text-sm font-semibold rounded-xl px-4 py-2.5 transition-colors"
          style={{ background: 'var(--accent)', boxShadow: '0 2px 12px rgba(15,76,76,0.18)' }}
          onMouseOver={e => (e.currentTarget.style.background = 'var(--accent-mid)')}
          onMouseOut={e => (e.currentTarget.style.background = 'var(--accent)')}
        >
          {t("startSession")}
        </button>
        <button
          onClick={() => router.push(`/sessions/${c.id}?patient=${encodeURIComponent(c.name)}`)}
          className="px-3 py-2.5 text-xs font-semibold rounded-xl border border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--surface-alt)] hover:text-[var(--text-primary)] transition-colors"
          title={t("viewHistory")}
        >
          {t("sessions")}
        </button>
        <button
          onClick={() => router.push(`/feedback/${c.id}?patient=${encodeURIComponent(c.name)}`)}
          className="px-3 py-2.5 text-xs font-semibold rounded-xl border border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--surface-alt)] hover:text-[var(--text-primary)] transition-colors"
        >
          {t("feedback")}
        </button>
      </div>
    </div>
  );
}
